import { useState, useEffect } from 'react';
import { FaFacebook, FaInstagram, FaLinkedin, FaTwitter, FaPlus, FaTrash, FaGlobe } from 'react-icons/fa';
import { toast } from 'react-toastify';
import TextEdit from '../../../molecules/TextEdit';
import TextArea from '../../../molecules/TextArea';
import SelectTextField from '../../../molecules/SelectTextField';
import CurrencyInput from '../../../molecules/CurrencyInput';
import useProvinces, { Province } from '../../../../hooks/useProvinces';
import useCities, { City } from '../../../../hooks/useCities';
import appConfig from '../../../../config/app-config';
import Dialog from '../../../organisms/Dialog';

const COMMUNICATION_ENV = appConfig.apis.communicationApiEnv;
const BASE_URL = COMMUNICATION_ENV.address;
const API_KEY = COMMUNICATION_ENV.key;

const SOCIAL_NETWORKS = [
  { value: 'website', label: 'Página da empresa', icon: FaGlobe },
  { value: 'facebook', label: 'Facebook', icon: FaFacebook },
  { value: 'instagram', label: 'Instagram', icon: FaInstagram },
  { value: 'linkedin', label: 'LinkedIn', icon: FaLinkedin },
  { value: 'twitter', label: 'Twitter', icon: FaTwitter },
];

const FAROL_OPTIONS = [
  { value: 'FAST', label: 'Rápido' },
  { value: 'WAITING', label: 'Aguardando retorno' },
  { value: 'LONG_TIME', label: 'Demora responder' },
  { value: 'MODERATELY_FAST', label: 'Moderadamente rápido' },
  { value: 'NO_RESPOND', label: 'Não responde' },
];

interface ContactModalEditProps {
  contactId: string;
  contactType: 'PF' | 'PJ';
  currentData: {
    name: string;
    taxId: string;
    taxRootId?: string;
    observation: string;
    farol: string;
    socialMedia?: {
      facebook?: string;
      instagram?: string;
      linkedin?: string;
      twitter?: string;
    };
    city?: string;
    state?: string;
    // PF fields
    nickname?: string;
    birthDate?: string;
    jobTitle?: string;
    // PJ fields
    segment?: string;
    employees?: string;
    revenue?: string;
    size?: string;
    isHeadquarters?: boolean;
  };
  onClose: () => void;
  onSuccess: (updatedContact: any) => void;
}

export default function ContactModalEdit({
  contactId,
  contactType,
  currentData,
  onClose,
  onSuccess,
}: ContactModalEditProps) {
  const [isSending, setIsSending] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const { fetchProvinces } = useProvinces();
  const { fetchCities } = useCities();
  const [provinces, setProvinces] = useState<Province[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [formData, setFormData] = useState({
    name: currentData.name,
    observation: currentData.observation,
    farol: currentData.farol,
    city: currentData.city || '',
    state: currentData.state || '',
    // PF
    nickname: currentData.nickname || '',
    birthDate: currentData.birthDate ? currentData.birthDate.split('/').reverse().join('-') : '',
    jobTitle: currentData.jobTitle || '',
    // PJ
    segment: currentData.segment || '',
    employees: currentData.employees || '',
    revenue: currentData.revenue || '',
    size: currentData.size || '',
    isHeadquarters: currentData.isHeadquarters || false,
  });

  const availableSocialNetworks = contactType === 'PF'
    ? SOCIAL_NETWORKS.filter(n => n.value !== 'website')
    : SOCIAL_NETWORKS;

  const [socialLinks, setSocialLinks] = useState<Array<{ network: string; url: string }>>([]);

  useEffect(() => {
    // Inicializar links sociais
    const links: Array<{ network: string; url: string }> = [];
    if (currentData.socialMedia) {
      Object.entries(currentData.socialMedia).forEach(([network, url]) => {
        if (url) links.push({ network, url });
      });
    }
    setSocialLinks(links);
  }, [currentData.socialMedia]);

  useEffect(() => {
    fetchProvinces().then(setProvinces).catch(() => toast.error('Erro ao carregar estados'));
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (formData.state) {
      setLoadingCities(true);
      fetchCities(formData.state).then(data => { if (!cancelled) setCities(data); }).catch(() => { if (!cancelled) toast.error('Erro ao carregar cidades'); }).finally(() => { if (!cancelled) setLoadingCities(false); });
    } else {
      setCities([]);
    }
    return () => { cancelled = true; };
  }, [formData.state]);

  const handleAddSocialLink = () => {
    setSocialLinks([...socialLinks, { network: 'facebook', url: '' }]);
  };

  const handleRemoveSocialLink = (index: number) => {
    setSocialLinks(socialLinks.filter((_, i) => i !== index));
  };

  const handleSocialLinkChange = (index: number, field: 'network' | 'url', value: string) => {
    const updated = [...socialLinks];
    updated[index][field] = value;
    setSocialLinks(updated);
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error('Nome é obrigatório');
      return;
    }

    setIsSending(true);
    setFieldErrors({});

    const payload: any = {
      fullName: formData.name,
      notes: formData.observation,
      status: formData.farol,
      city: formData.city,
      state: formData.state,
    };

    // Social media
    const socialMedia: any = {};
    socialLinks.forEach(link => {
      const url = link.url.trim();
      if (url && /^https?:\/\//i.test(url)) {
        socialMedia[link.network] = url;
      }
    });
    if (Object.keys(socialMedia).length > 0) {
      payload.socialMedia = socialMedia;
    }

    // PF specific
    if (contactType === 'PF') {
      payload.nickname = formData.nickname || null;
      payload.birthDate = formData.birthDate || null;
      payload.jobTitle = formData.jobTitle || null;
    }

    // PJ specific
    if (contactType === 'PJ') {
      payload.segment = formData.segment || null;
      payload.employees = formData.employees || null;
      payload.revenue = formData.revenue || null;
      payload.size = formData.size || null;
      payload.isHeadquarters = formData.isHeadquarters;
    }

    try {
      const session = await (await import('aws-amplify')).Auth.currentSession();
      const token = session.getIdToken().getJwtToken();
      const response = await fetch(`${BASE_URL}/contacts/${contactId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': API_KEY || '',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const result = await response.json();
        toast.success('Contato atualizado com sucesso!');
        onSuccess(result);
        onClose();
      } else {
        const error = await response.json();
        if (error.errors?.length) {
          const errors: Record<string, string> = {};
          error.errors.forEach((e: any) => { errors[e.field] = e.message; });
          setFieldErrors(errors);
        }
        toast.error(error.debugMessage || error.message || 'Erro ao atualizar contato');
      }
    } catch (error) {
      toast.error('Erro ao atualizar contato');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Dialog
      id="contact-edit-modal"
      show={true}
      onClose={onClose}
      header="Editar Contato"
      size="lg"
      body={
        <div className="p-6 space-y-6">
          {/* Dados Principais */}
          <div>
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <span className="block text-sm font-medium text-gray-500 dark:text-gray-400">Tipo</span>
                  <span className="text-sm text-gray-900 dark:text-white">{contactType === 'PF' ? 'Pessoa Física' : 'Pessoa Jurídica'}</span>
                </div>
                <div>
                  <span className="block text-sm font-medium text-gray-500 dark:text-gray-400">{contactType === 'PF' ? 'CPF' : 'CNPJ'}</span>
                  <span className="text-sm text-gray-900 dark:text-white">{currentData.taxId}</span>
                </div>
                <SelectTextField
                  label="Status (Farol)"
                  value={formData.farol}
                  onChange={(e) => setFormData({ ...formData, farol: e.target.value })}
                  items={FAROL_OPTIONS}
                  emptyLabel="Selecione..."
                />
              </div>

              {contactType === 'PJ' && currentData.taxRootId && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    CNPJ Raiz
                  </label>
                  <input
                    type="text"
                    value={currentData.taxRootId}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-gray-400"
                  />
                </div>
              )}

              <TextEdit
                label="Nome"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder=""
              />

              <TextArea
                label="Observação"
                value={formData.observation}
                onChange={(e) => setFormData({ ...formData, observation: e.target.value })}
                rows={3}
                placeholder=""
              />

            </div>
          </div>

          {/* Campos específicos PF */}
          {contactType === 'PF' && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Informações Pessoais</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <TextEdit
                    label="Apelido"
                    value={formData.nickname}
                    onChange={(e) => setFormData({ ...formData, nickname: e.target.value })}
                    placeholder=""
                  />
                  <TextEdit
                    label="Cargo"
                    value={formData.jobTitle}
                    onChange={(e) => setFormData({ ...formData, jobTitle: e.target.value })}
                    placeholder=""
                  />
                  <TextEdit
                    label="Data de Aniversário"
                    type="date"
                    value={formData.birthDate}
                    onChange={(e) => setFormData({ ...formData, birthDate: e.target.value })}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Campos específicos PJ */}
          {contactType === 'PJ' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Informações da Empresa</h3>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.isHeadquarters}
                    onChange={(e) => setFormData({ ...formData, isHeadquarters: e.target.checked })}
                    className="w-4 h-4 text-blue-600 border-gray-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">É Matriz</span>
                </label>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <TextEdit
                  label="Segmento"
                  value={formData.segment}
                  onChange={(e) => setFormData({ ...formData, segment: e.target.value })}
                />
                <TextEdit
                  label="Porte"
                  value={formData.size}
                  onChange={(e) => setFormData({ ...formData, size: e.target.value })}
                />
                <TextEdit
                  label="Total de Funcionários"
                  type="number"
                  value={formData.employees}
                  onChange={(e) => setFormData({ ...formData, employees: e.target.value })}
                  errorMessage={fieldErrors['employees']}
                />
                <CurrencyInput
                  label="Faturamento Anual"
                  prefix={<span className="text-sm text-gray-500">R$</span>}
                  fractionDigits={2}
                  value={formData.revenue ? Number(formData.revenue) : 0}
                  onChange={(value) => setFormData({ ...formData, revenue: String(value) })}
                  errorMessage={fieldErrors['revenue']}
                />
              </div>
            </div>
          )}

          {/* Endereço */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Endereço</h3>
            <div className="grid grid-cols-2 gap-4">
              <SelectTextField
                label="Estado (UF)"
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value, city: '' })}
                items={provinces.map(p => ({ value: p.initials, label: p.name }))}
                emptyLabel="Selecione..."
              />
              <SelectTextField
                label="Cidade"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                items={cities.map(c => ({ value: c.name, label: c.name }))}
                emptyLabel="Selecione..."
                isLoading={loadingCities}
              />
            </div>
          </div>

          {/* Links Sociais */}
          <div className="border border-gray-200 dark:border-slate-700 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white">Links Sociais</h3>
              <button
                onClick={handleAddSocialLink}
                className="p-1.5 border border-gray-400 dark:border-gray-600 bg-gray-50 dark:bg-slate-700 text-gray-600 dark:text-gray-400 rounded-full hover:bg-gray-100 dark:hover:bg-slate-600"
              >
                <FaPlus size={10} />
              </button>
            </div>
            <div className="space-y-2">
              {socialLinks.map((link, index) => (
                <div key={index} className="flex gap-3 items-end">
                  <SelectTextField
                    label="Rede"
                    value={link.network}
                    onChange={(e) => handleSocialLinkChange(index, 'network', e.target.value)}
                    items={availableSocialNetworks.map(n => ({ value: n.value, label: n.label }))}
                    emptyLabel="Selecione..."
                    className="w-40"
                  />
                  <div className="flex-1">
                    <TextEdit
                      label="URL"
                      value={link.url}
                      onChange={(e) => handleSocialLinkChange(index, 'url', e.target.value)}
                      placeholder=""
                    />
                  </div>
                  <button
                    onClick={() => handleRemoveSocialLink(index)}
                    className="p-2 border border-red-400 dark:border-red-600 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-full hover:bg-red-100 dark:hover:bg-red-900/40"
                  >
                    <FaTrash size={12} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      }
      footer={
        <div className="flex gap-3">
          <button onClick={onClose} disabled={isSending} className="px-4 py-2 bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-slate-600 disabled:opacity-50">Cancelar</button>
          <button onClick={handleSubmit} disabled={isSending} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">{isSending ? 'Salvando...' : 'Salvar'}</button>
        </div>
      }
    />
  );
}
